import _ from 'lodash'

import { evaluateFormula } from './evaluation'
import { Query } from '../models/query'

/**
 * Resolves a select/tags field's dropdown options from its `optionSource`, in
 * precedence order: (1) `referenceList` — a firm-owned vocabulary
 * (`core_config_referenceList`); (2) `distinctValues` — values already stored on
 * the field (grouped query); (3) the field's static `enum`/`suggestions`
 * (handled by the caller). Platform-agnostic; the React hook lives in the
 * webapp, mobile calls these directly. See schemas/entities/config/referenceList.json.
 */

export const REFERENCE_LIST_ENTITY_TYPE = '/1.0/entities/metadata/core_config_referenceList.json'
const KEY_PATH = 'core_config_referenceList.key'
const ITEMS_PATH = 'core_config_referenceList.items'
const OWNER_FIRM_ID_PATH = 'owner.firm.entityId'
const DEFAULT_DISTINCT_VALUES_SIZE = 100
/** A firm's reference lists are few; one page is enough to know which keys are taken. */
const EXISTING_KEYS_SIZE = 500

export interface IReferenceListOption {
  label: string
  value: string
}

export interface IOptionSource {
  /** Key of a firm-owned reference list to resolve options from. */
  referenceList?: string
  /**
   * Display name for the list this field binds to, shown when offering the key as
   * something to create. Defaults to the humanized key.
   */
  referenceListLabel?: string
  /**
   * Offer the *eligible keys* rather than a list's values — the authoring side.
   * Set on `core_config_referenceList.key` so the "New Reference List" picker is
   * derived from the fields that declared `referenceList`, never from a registry.
   * See collectReferenceListKeys.
   */
  referenceListKeys?: boolean
  /** When true, suggest the distinct values already stored on the bound field. */
  distinctValues?: boolean
  /**
   * Edge path whose target's OWNER firm is the vocabulary authority — set this
   * for counterparty-owned entities where the record owner isn't the firm whose
   * list you want (e.g. a carrier-owned check-in execution whose list belongs to
   * the facility). Falls back to the entity's owner firm, then the session firm,
   * when unset or unresolvable. See resolveReferenceListFirmId.
   */
  referenceListFirmEdge?: string
}

/**
 * Reads a field's `optionSource`, from an explicit prop or (the common case) the
 * bound schema property via the `dataSchema` frames context.
 */
export const getOptionSource = (props: { optionSource?: IOptionSource; frames?: any }): IOptionSource | undefined => {
  if (props?.optionSource) {
    return props.optionSource
  }
  const dataSchema = props?.frames?.getContext?.('dataSchema')
  return dataSchema?.optionSource
}

/** Convenience: the reference-list key of a field's optionSource, if any. */
export const getReferenceListKey = (props: { optionSource?: IOptionSource; frames?: any }): string | undefined =>
  getOptionSource(props)?.referenceList

/** Resolves the entity-type id for the entity a field is bound to (for value queries). */
export const resolveEntityTypeId = (entity: any, entityType: string | undefined, valuePath: string[]): string => {
  if (entityType) {
    return entityType
  }
  const namespace = _.first(valuePath)
  const schema = _.find(entity?.schemas, (s) => _.get(s, 'metadata.namespace') === namespace)
  return schema?.id ?? `/1.0/entities/metadata/${namespace}.json`
}

/**
 * The firm that owns the entity being edited — the list resolves against this
 * firm, not the session firm, so a cross-firm switcher sees the owner firm's
 * vocabulary. Undefined for a brand-new entity (query falls back to the session firm).
 */
export const getEntityOwnerFirmId = (entity: any): string | undefined =>
  entity?.get?.(OWNER_FIRM_ID_PATH) ?? _.get(entity, OWNER_FIRM_ID_PATH)

/**
 * Resolves the firm whose reference list governs a field on the fallback ladder:
 * owner firm of the entity at `referenceListFirmEdge` (resolved store-first,
 * cache-first) → this entity's owner firm → session firm. Any miss degrades to
 * the next rung. See {@link IOptionSource.referenceListFirmEdge}.
 */
export const resolveReferenceListFirmId = (
  apis: any,
  entity: any,
  optionSource?: IOptionSource,
): Promise<string | undefined> => {
  const ownerFirmId = getEntityOwnerFirmId(entity)
  const edgePath = optionSource?.referenceListFirmEdge
  if (!edgePath) {
    return Promise.resolve(ownerFirmId)
  }
  const edge = entity?.get?.(edgePath) ?? _.get(entity, edgePath)
  const targetId = _.get(edge, 'entityId')
  const store = apis?.getStore?.()
  if (!targetId || !store?.getOrFetchRecord) {
    return Promise.resolve(ownerFirmId)
  }
  return Promise.resolve(store.getOrFetchRecord(targetId))
    .then((target: any) => getEntityOwnerFirmId(target) ?? ownerFirmId)
    .catch(() => ownerFirmId)
}

/** Maps a reference list's raw items to sorted, active {value,label} options. */
export const mapReferenceListItemsToOptions = (items: any[]): IReferenceListOption[] =>
  _.chain(items)
    .filter((item) => item?.isActive !== false && _.isString(item?.value) && item.value !== '')
    .sortBy((item) => (_.isFinite(item?.sortOrder) ? item.sortOrder : Number.MAX_SAFE_INTEGER))
    .map((item) => ({
      value: item.value,
      label: _.isString(item?.label) && item.label !== '' ? item.label : item.value,
    }))
    .value()

/**
 * Loads a firm's reference list by key as active {value,label} options, scoped to
 * `firmId` when given (else the session firm). Resolves to [] when no list is
 * configured, so callers can fall back to static suggestions / free-text.
 */
export const fetchReferenceListOptions = (
  apis: any,
  referenceListKey?: string,
  firmId?: string,
): Promise<IReferenceListOption[]> => {
  if (!referenceListKey) {
    return Promise.resolve([])
  }
  const query = new Query(apis)
    .setEntityType(REFERENCE_LIST_ENTITY_TYPE)
    .setFilters([{ type: 'match', path: KEY_PATH, value: referenceListKey }])
    .setSize(1)
    .setDebug({ context: 'referenceListOptions' })
  if (firmId) {
    query.setFirm(firmId)
  }
  return query
    .getCollection()
    .find()
    .then((entities: any[]) => mapReferenceListItemsToOptions(_.get(_.first(entities), ITEMS_PATH) ?? []))
    .catch(() => [])
}

/* --------------------------------------------------------------------------
 * Authoring side — which lists a firm can create.
 *
 * A field opting in via `optionSource.referenceList` is the ONLY declaration of
 * a bindable key; there is deliberately no registry on the reference list itself
 * (see referenceList.json). Eligibility is derived from the firm's own loaded
 * schemas, so it tracks bundle membership: base capabilities shipped to that
 * firm plus its custom schemas, never another firm's.
 * ------------------------------------------------------------------------ */

export interface IReferenceListKeyOption {
  /** The key a field binds to, e.g. 'trailerTags'. */
  value: string
  /** Picker label, e.g. 'Trailer Tags — Trailer'. */
  label: string
  /** Where it's declared, e.g. 'Trailer · Tags'. */
  usedBy?: string
}

/** 'trailerTags' -> 'Trailer Tags'. */
export const humanizeReferenceListKey = (key: string): string => _.startCase(key)

/**
 * Subtrees that never hold a data declaration. `uiSchema` matters most: a
 * `ui:selectField` can carry its own `optionSource`, which would double-count a
 * key under the wrong provenance.
 */
const NON_DATA_KEYS = new Set(['uiSchema', 'translations'])

/**
 * Recurses a schema collecting `optionSource.referenceList` declarations.
 *
 * The traversal is generic rather than `properties`-only on purpose: a field can
 * be declared under `definitions` and merely `$ref`'d from `properties` — that is
 * exactly where `trailerType` lives on the trailer schema — and declarations also
 * appear below `items`, `allOf`, and friends. A child's own `label` is read
 * before recursing, so provenance names the field rather than its namespace.
 *
 * First declaration of a key wins; callers sort the input so that is stable.
 */
const collectFromNode = (
  node: any,
  schemaTitle: string | undefined,
  fieldLabel: string | undefined,
  out: Map<string, IReferenceListKeyOption>,
  seen: WeakSet<object>
): void => {
  if (!_.isObject(node) || seen.has(node)) {
    return
  }
  seen.add(node)

  const optionSource: IOptionSource | undefined = _.get(node, 'optionSource')
  const key = optionSource?.referenceList
  if (_.isString(key) && key !== '' && !out.has(key)) {
    const name = optionSource?.referenceListLabel ?? humanizeReferenceListKey(key)
    const usedBy = _.compact([schemaTitle, fieldLabel]).join(' · ')
    out.set(key, {
      value: key,
      label: schemaTitle ? `${name} — ${schemaTitle}` : name,
      usedBy: usedBy === '' ? undefined : usedBy,
    })
  }

  _.forEach(node, (child, childKey) => {
    if (NON_DATA_KEYS.has(childKey as string) || childKey === 'optionSource' || !_.isObject(child)) {
      return
    }
    collectFromNode(child, schemaTitle, _.get(child, 'label') ?? fieldLabel, out, seen)
  })
}

/**
 * The keys any field in these schemas binds to — i.e. every list that could
 * exist. Takes raw schema JSON: callers holding store Entities must unwrap
 * `.content` first (the store caches Entity proxies, not plain objects).
 *
 * Schemas are sorted by title first so that when several declare the same key
 * (trailerType is bound by both the trailer and the YMS storyboard), the winning
 * provenance is the same for every firm regardless of bundle order.
 */
export const collectReferenceListKeys = (schemas: any[]): IReferenceListKeyOption[] => {
  const out = new Map<string, IReferenceListKeyOption>()
  const ordered = _.sortBy(_.filter(schemas, _.isObject), (schema) => _.toLower(_.get(schema, 'title') ?? ''))
  // A fresh `seen` per schema: two schemas may share a spliced-in mixin subtree,
  // and one shared set would silently stop walking the second.
  _.forEach(ordered, (schema) =>
    collectFromNode(schema, _.get(schema, 'title'), undefined, out, new WeakSet<object>())
  )
  return _.sortBy([...out.values()], 'label')
}

/** Keys the firm already has a list for, so they aren't offered again. */
export const fetchExistingReferenceListKeys = (apis: any, firmId?: string): Promise<string[]> => {
  const query = new Query(apis)
    .setEntityType(REFERENCE_LIST_ENTITY_TYPE)
    .setSize(EXISTING_KEYS_SIZE)
    .setDebug({ context: 'referenceListExistingKeys' })
  if (firmId) {
    query.setFirm(firmId)
  }
  return query
    .getCollection()
    .find()
    .then((entities: any[]) => _.compact(_.map(entities, (entity) => _.get(entity, KEY_PATH))))
    .catch(() => [])
}

/**
 * Eligible keys minus the ones already created. Compared case-insensitively to
 * match the per-firm uniqueness constraint on `lower(key)`.
 */
export const getAvailableReferenceListKeys = (
  schemas: any[],
  existingKeys: string[]
): IReferenceListKeyOption[] => {
  const taken = new Set(_.map(existingKeys, (key) => _.toLower(key)))
  return _.reject(collectReferenceListKeys(schemas), (option) => taken.has(_.toLower(option.value)))
}

/** Maps grouped-query buckets to distinct {value,label} options. */
export const mapDistinctBuckets = (buckets: any[]): IReferenceListOption[] =>
  _.compact(
    _.map(buckets, (bucket) => {
      const value = bucket?.data
      return _.isString(value) && value ? { label: value, value } : null
    })
  )

/**
 * Loads the distinct values already stored on a field (grouped query on the bound
 * path) so it can self-suggest from prior usage, scoped to `firmId` when given.
 * Resolves to [] when there is no value path.
 *
 * Note: array-of-primitive fields key under the bare dotted path
 * (`core_yms_trailer.tags`) in the EQL registry — no `[*]` suffix (that belongs
 * to the separate search-schema path system).
 */
export const fetchDistinctFieldValues = (
  apis: any,
  entityType: string,
  valuePath: string[],
  size?: number,
  firmId?: string,
): Promise<IReferenceListOption[]> => {
  if (_.isEmpty(valuePath) || !entityType) {
    return Promise.resolve([])
  }
  const query = new Query(apis)
    .setEntityType(entityType)
    .setGroups([{ path: valuePath.join('.'), type: 'field' }])
    .setShouldIncludeLeafEntities(false)
    .setSize(size ?? DEFAULT_DISTINCT_VALUES_SIZE)
    .setDebug({ context: 'optionSource--distinctValues' })
  if (firmId) {
    query.setFirm(firmId)
  }
  return query
    .getCollection()
    .find()
    .then((buckets: any[]) => mapDistinctBuckets(buckets))
    .catch(() => [])
}

/**
 * Normalizes creatable multi-select values via a `transform` formula (e.g.
 * 'UPPER(TO_IDENTIFIER(arg0))') — deduped, empties dropped. Non-array values pass
 * through unchanged.
 */
export const applyTagTransform = (transform: string | undefined, values: any): any => {
  if (!transform || !_.isArray(values)) {
    return values
  }
  const next = _.uniq(_.map(values, (value) => (_.isString(value) ? evaluateFormula(transform, value) ?? value : value)))
  return _.reject(next, (value) => value === '')
}
